import React, { useState } from 'react';
import RoomHeader from './RoomHeader';
import MatchSettings from './MatchSettings';
import MatchSummaryBox from './MatchSummaryBox';
import AvatarPicker from '../common/AvatarPicker'; 
import ParticleBackground from '../common/ParticleBackground';
import CyberLoader from '../common/CyberLoader';
import ThemeSelect from '../common/ThemeSelect';

import './WaitingRoom.css';

export default function WaitingRoom({ 
  gameState, roomId, myPlayerId, send, error, clearError, tunnelUrl, isSpectator, networkMode 
}) {
  const [pendingStartWithNoScore, setPendingStartWithNoScore] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  console.log("🏠 [WaitingRoom] Render:", { 
    tunnelUrl, 
    roomId, 
    networkMode,
    gameStateTunnelUrl: gameState?.tunnel_url,
    gameStateStatus: gameState?.status,
    gameStatePhase: gameState?.phase 
  });

  if (!gameState) {
    return (
      <div className="waiting-room-layout loading-state">
        <ParticleBackground />
        <div className="glass-panel text-center" style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ color: '#d4af37' }}>🃏 Loading Room...</h2>
        </div>
      </div>
    );
  }

  const actualRoomId = roomId || gameState.room_id || gameState.roomId;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDecoupled = !!process.env.REACT_APP_BACKEND_URL;
  const baseUrl = (tunnelUrl && !isDecoupled) ? tunnelUrl.replace(/\/$/, '') : (!isLocal || isDecoupled ? window.location.origin : '');
  const shareLink = (baseUrl && actualRoomId) ? `${baseUrl}/?room=${actualRoomId}` : '';

  const copyToClipboard = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const mode = gameState.mode || '28';
  const settings = { 
    deal_mode: 'full', 
    allow_doubling: false,
    royal_pair: false, 
    sprint_rounds: gameState.sprint_rounds || 0,
    escalation_timer: gameState.escalation_timer || false,
    ...(gameState.settings || {}) 
  };
  const isPoker = mode === 'poker';
  const isSoloGame = mode === 'poker' || mode === 'donkey';
  const maxPlayers = (mode === '56' || (isSoloGame && settings.player_count === 6)) ? 6 : 4; 
   
  const hostId = gameState.host_id;
  const isHost = hostId === myPlayerId;
  
  const players = Array.from({ length: maxPlayers }, (_, i) => gameState.players?.[i] || null);
  const count = players.filter(Boolean).length;
  
  // Donkey allows starting with n-1 players
  let canStart = count === maxPlayers;
  if (mode === 'donkey') {
    canStart = count >= maxPlayers - 1;
  }

  const myPlayer = players.find(p => p?.id === myPlayerId);
  const takenAvatars = players.filter(Boolean).map(p => p.avatar);

  const isCards = mode !== 'ludo';

  return (
    <div className={`waiting-room-layout ${isPoker ? 'poker-theme' : ''} ${!isCards ? 'boards-theme' : ''}`} style={{
      background: 'radial-gradient(ellipse at center, #064e3b 0%, #022c22 100%)' 
    }}>
      <ParticleBackground category={isCards ? 'cards' : 'boards'} />
      
      {/* COLUMN 1: LEFT - BRANDING */}
      <aside className="column left-col" style={{ position: 'relative', zIndex: 1 }}>
        <div className="col-inner text-center">
          <div className="branding" style={{ marginBottom: 0 }}>
            <img 
              src={isCards ? "/assets/resources/The-Cheetu-Club.svg" : "/assets/resources/The-Board-Game-Club.svg"} 
              alt={isCards ? "The Cheetu Club" : "The Board Room"} 
              className="brand-logo-img" 
              style={{ margin: '0 auto' }}
              onError={(e) => { e.target.style.display = 'none'; }} 
            />

            <div style={{ marginTop: '30px' }}>
              {/* SocialLinks removed - now centralized in footer */}
            </div>
          </div>
        </div>
      </aside>

      {/* COLUMN 2: CENTER - CONFIG & INVITE */}
      <main className="column center-col" style={{ position: 'relative', zIndex: 1 }}>
        <div className="col-inner" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          
          <div className="glass-panel">
            <h2 className="panel-title">Game Rules</h2>
            <MatchSettings 
              mode={mode} 
              settings={settings} 
              send={send} 
              isHost={isHost && !isSpectator}
            />
            <MatchSummaryBox mode={mode} settings={settings} />
          </div>

          <div className="glass-panel invite-panel text-center">
             <h2 className="panel-title" style={{ fontSize: '1rem', marginBottom: '10px' }}>
               {networkMode === 'local' ? 'Local Room' : 'Invite Friends'}
             </h2>
             
             {networkMode === 'online' && !tunnelUrl && !isDecoupled && (
               <div style={{ padding: '20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                 <CyberLoader />
                 <p style={{ fontSize: '0.8rem', color: '#d4af37', opacity: 0.8 }}>Generating secure invite link...</p>
               </div>
             )}

             {networkMode === 'online' && (tunnelUrl || isDecoupled) && (
               <div className="share-box">
                 <input readOnly value={shareLink} className="share-input" />
                 <button 
                  className={`btn-copy ${copied ? 'copied' : ''}`} 
                  onClick={copyToClipboard}
                 >
                   {copied ? '✓ Copied' : 'Copy Link'}
                 </button>
               </div>
             )}

             {networkMode === 'local' && (
               <div style={{ padding: '10px 0', opacity: 0.8 }}>
                 <p style={{ fontSize: '0.85rem', color: '#a7f3d0' }}>
                   Playing on local network. Friends can join using the code below if they are on the same Wi-Fi.
                 </p>
               </div>
             )}

            <div style={{ marginTop: '15px', opacity: 0.8 }}>
              <RoomHeader mode={mode} roomId={actualRoomId} />
            </div>
          </div>

          {isSpectator && (
            <div className="glass-panel text-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3b82f6' }}>
              <p style={{ color: '#60a5fa', margin: 0, fontWeight: 'bold' }}>👁 SPECTATOR MODE</p>
              <p style={{ color: 'white', fontSize: '0.8rem', opacity: 0.8, marginTop: '5px' }}>
                The room is full. You can take over a bot's seat or wait for someone to leave.
              </p>
            </div>
          )}

        </div>
      </main>

      {/* COLUMN 3: RIGHT - HERO ROSTER */}
      <aside className="column right-col" style={{ position: 'relative', zIndex: 1 }}>
        <div className="col-inner glass-panel text-center">
          
          <div className="hero-roster-grid">
            {Array.from({ length: maxPlayers }).map((_, i) => {
              const p = players[i];
              const isTeamA = i % 2 === 0;
              
              let teamColor = isSoloGame ? '#fbbf24' : (isTeamA ? '#ef4444' : '#3b82f6');
              let teamName = isSoloGame ? `Seat ${i + 1}` : `Team ${isTeamA ? 'A' : 'B'}`;

              if (mode === 'ludo') {
                const ludoColors = ['#ff4d4d', '#2ecc71', '#f1c40f', '#3498db'];
                const ludoNames = ['Red', 'Green', 'Yellow', 'Blue'];
                teamColor = ludoColors[i];
                teamName = ludoNames[i];
              }
              
              /* --- EMPTY SLOT UI --- */
              if (!p) return (
                <div key={i} className="hero-slot empty">
                  <div 
                    className={`empty-slot-click-area ${isSpectator ? 'disabled' : ''}`} 
                    onClick={() => !isSpectator && send('change_slot', { target_id: i })}
                    style={{ cursor: isSpectator ? 'default' : 'pointer' }}
                  >
                    <div className="plus-icon">+</div>
                    <span className="team-text">{mode === 'ludo' ? `Join ${teamName}` : (isSoloGame ? 'Join Seat' : `Join Team ${isTeamA ? 'A' : 'B'}`)}</span>
                  </div>
                  
                  {isHost && !isSpectator && (
                    <button 
                      className="btn-add-bot-inline" 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        send('add_bot', { difficulty: 'easy', target_id: i }); 
                      }}
                    >
                      + Add Bot
                    </button>
                  )}
                </div>
              );

              /* --- FILLED SLOT UI --- */
              // Bulletproof check for Go JSON casing variations and fallbacks
              const isBot = p.is_bot === true || 
                            p.IsBot === true || 
                            p.isBot === true || 
                            p.id < 0 || 
                            (p.name && p.name.toLowerCase().includes("bot"));

              return (
                <div key={i} className="hero-slot filled" style={{ borderTop: `4px solid ${teamColor}` }}>
                  <div className="avatar-frame" style={{ 
                    width: p.avatar.startsWith('Mal_') ? '130px' : '110px',
                    height: p.avatar.startsWith('Mal_') ? '130px' : '110px'
                  }}>
                    <img 
                      src={p.avatar.startsWith('Mal_') ? `/assets/avatars/malayalam/${p.avatar}.webp` : `/assets/avatars/default/${p.avatar}.webp`} 
                      alt="Avatar" 
                      className="portrait" 
                    />
                    
                    {/* Human Player Edit Badge */}
                    {p.id === myPlayerId && (
                      <button className="edit-badge" onClick={() => setIsPickerOpen(true)}>✎ Edit</button>
                    )}

                    {/* Player Removal Badge (Host Only) */}
                    {isHost && !isSpectator && p.id !== myPlayerId && (
                      <button 
                        className="remove-bot-badge" 
                        onClick={() => send('kick_player', { target_id: i })}
                      >
                        ✕ Remove
                      </button>
                    )}

                  </div>
                  <div className="player-meta" style={{ width: '100%' }}>
                    <p className="name">{p.name} {p.id === hostId ? '👑' : ''}</p>
                    <p className="team" style={{ color: teamColor }}>{isSoloGame ? `Seat ${i + 1}` : `Team ${isTeamA ? 'A' : 'B'}`}</p>
                    
                    {/* --- THE BOT TAKE OVER UI --- */}
                    {isSpectator && isBot && (
                      <button 
                        className="btn-takeover-waiting"
                        onClick={() => send('claim_bot_seat', { target_id: p.id })}
                        style={{
                          marginTop: '8px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        Take Over
                      </button>
                    )}

                    {/* --- THE BOT DIFFICULTY UI --- */}
                    {isBot && !isSpectator && (
                      <div className="bot-controls">
                        {isHost ? (
                          <ThemeSelect 
                            value={p.bot_difficulty || "easy"} 
                            onChange={(e) => send('change_bot_difficulty', { target_id: p.id, difficulty: e.target.value })}
                            options={[
                              { value: 'easy', label: 'Easy Bot' },
                              { value: 'medium', label: 'Medium Bot' },
                              { value: 'hard', label: 'Hard Bot' },
                              { value: 'grandmaster', label: 'Grandmaster' }
                            ]}
                          />
                        ) : (
                          <span className={`bot-difficulty-badge ${p.bot_difficulty || "easy"}`}>
                            {p.bot_difficulty || "easy"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="action-area">
            {error && <div className="error-toast" onClick={clearError}>⚠ {error}</div>}

            {isHost && !isSpectator ? (
              <>
                {(mode === '28' || mode === '56') && !pendingStartWithNoScore && (
                  <button 
                    className="btn-gold-secondary"
                    onClick={() => send('randomize_teams', {})}
                  >
                    🎲 Randomize Teams
                  </button>
                )}
                
                {pendingStartWithNoScore ? (
                  <div style={{ padding: '16px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(212, 175, 55, 0.2)', borderRadius: '12px', marginBottom: '16px' }}>
                    <h3 style={{ color: '#d4af37', margin: '0 0 10px 0', fontSize: '1.1rem' }}>Score Tracking Disabled</h3>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 15px 0' }}>
                      This match will be unranked. Do you want to proceed?
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button 
                        className="btn-gold-secondary" 
                        style={{ margin: 0, flex: 1 }}
                        onClick={() => setPendingStartWithNoScore(false)}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn-gold-primary" 
                        style={{ margin: 0, flex: 1 }}
                        onClick={() => {
                          setPendingStartWithNoScore(false);
                          send('start_game', {});
                        }}
                      >
                        Start Game
                      </button>
                    </div>
                  </div>
                ) : (
                  <button 
                    className="btn-gold-primary" 
                    onClick={() => {
                      if (settings.track_score === false) {
                        setPendingStartWithNoScore(true);
                      } else {
                        send('start_game', {});
                      }
                    }}
                    disabled={!canStart}
                  >
                    {canStart ? 'Start Game' : 'Waiting for players...'}
                  </button>
                )}
              </>
            ) : (
              <div className="host-waiting-pill">
                <p style={{ margin: 0 }}>
                  {isSpectator ? 'You are spectating this room.' : 'Waiting for the host to start the game...'}
                </p>
              </div>
            )}
            
            <button 
              onClick={() => send('leave_room', {})}
              style={{
                marginTop: '15px',
                background: 'transparent',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                color: '#ef4444',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                width: '100%',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                e.target.style.border = '1px solid rgba(239, 68, 68, 0.8)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.border = '1px solid rgba(239, 68, 68, 0.5)';
              }}
            >
              Leave Room
            </button>
          </div>

        </div>
      </aside>

      {/* MODAL */}
      {isPickerOpen && (
        <AvatarPicker 
          currentAvatar={myPlayer?.avatar}
          takenAvatars={takenAvatars}
          onSelect={(avatar) => send('update_avatar', { avatar })}
          onClose={() => setIsPickerOpen(false)}
        />
      )}

    </div>
  );
}